/**
 * Subscriber Details Page
 * Full-page subscriber detail view
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Package,
  CreditCard,
  Building,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  ExternalLink,
} from 'lucide-react';

import { subscribersApi } from '../api/subscribers.api';
import { StatusBadge } from '../components/StatusBadge';
import {
  resolveDisplayUsername,
  resolveNetworkUsername,
} from '../lib/subscriberIdentity';

export function SubscriberDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'history'>('overview');

  const { data: subscriber, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriber', id],
    queryFn: () => subscribersApi.get(id!),
    enabled: !!id,
  });

  const { data: reminderHistory, isLoading: reminderHistoryLoading } = useQuery({
    queryKey: ['subscriber', id, 'reminder-history'],
    queryFn: () => subscribersApi.getReminderHistory(id!),
    enabled: !!id && activeTab === 'history',
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => subscribersApi.syncSingle(id!),
    onSuccess: (result) => {
      toast.success(`Subscriber ${result.action === 'created' ? 'created' : 'updated'}`);
      queryClient.invalidateQueries({ queryKey: ['subscriber', id] });
    },
    onError: () => {
      toast.error('Sync failed');
    },
  });

  // Pause reminders mutation
  const pauseMutation = useMutation({
    mutationFn: (reason?: string) => subscribersApi.pauseReminders(id!, reason),
    onSuccess: () => {
      toast.success('Reminders paused');
      queryClient.invalidateQueries({ queryKey: ['subscriber', id] });
    },
    onError: () => {
      toast.error('Failed to pause reminders');
    },
  });

  // Resume reminders mutation
  const resumeMutation = useMutation({
    mutationFn: () => subscribersApi.resumeReminders(id!),
    onSuccess: () => {
      toast.success('Reminders resumed');
      queryClient.invalidateQueries({ queryKey: ['subscriber', id] });
    },
    onError: () => {
      toast.error('Failed to resume reminders');
    },
  });

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">Loading subscriber details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <p className="text-red-500 text-lg">Failed to load subscriber</p>
        <div className="flex justify-center gap-4 mt-4">
          <button
            onClick={() => refetch()}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Try again
          </button>
          <button
            onClick={() => navigate('/subscribers')}
            className="text-gray-600 hover:text-gray-800 font-medium"
          >
            Back to list
          </button>
        </div>
      </div>
    );
  }

  if (!subscriber) {
    return (
      <div className="p-12 text-center">
        <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">Subscriber not found</p>
        <button
          onClick={() => navigate('/subscribers')}
          className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
        >
          Back to list
        </button>
      </div>
    );
  }

  const isEligible = subscriber.status.normalized === 'eligible';
  const hasOutstanding = subscriber.billing.outstandingAmount > 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/subscribers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Subscribers
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-3xl">
              {subscriber.fullname?.charAt(0).toUpperCase() || '?'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {subscriber.fullname || 'Unknown Subscriber'}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <StatusBadge status={subscriber.status} />
                {subscriber.reminderPaused && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    <PauseCircle className="w-3 h-3" />
                    Reminders Paused
                  </span>
                )}
                {subscriber.deletedInCrm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    Deleted in CRM
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sync from CRM
            </button>

            {!subscriber.deletedInCrm && (
              <>
                {subscriber.reminderPaused ? (
                  <button
                    onClick={() => resumeMutation.mutate()}
                    disabled={resumeMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Resume Reminders
                  </button>
                ) : (
                  <button
                    onClick={() => pauseMutation.mutate(undefined)}
                    disabled={pauseMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                  >
                    <PauseCircle className="w-4 h-4" />
                    Pause Reminders
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {(['overview', 'billing', 'history'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-sm font-medium capitalize border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              {/* Contact Information */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-400" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Email</label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {subscriber.email || 'Not provided'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Phone</label>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className={subscriber.phone.valid ? 'text-gray-900' : 'text-red-600'}>
                        {subscriber.phone.formatted}
                      </span>
                      {subscriber.phone.valid ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <span title={subscriber.phone.validationError || 'Invalid phone'}>
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        </span>
                      )}
                    </div>
                    {subscriber.phone.raw !== subscriber.phone.normalized && (
                      <p className="text-xs text-gray-500 mt-1">
                        Raw: {subscriber.phone.raw}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Company</label>
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {subscriber.companyName || 'Not provided'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Subscriber Code</label>
                    <span className="text-gray-900">{subscriber.subscriberCode || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* CRM Details */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-gray-400" />
                  CRM Details
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">CRM Subscriber ID</label>
                    <span className="text-gray-900">{subscriber.crmSubscriberId}</span>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">CRM Site ID</label>
                    <span className="text-gray-900">{subscriber.site.crmSiteId}</span>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Site Name</label>
                    <span className="text-gray-900">
                      {subscriber.site.name || `Site ${subscriber.site.crmSiteId}`}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Username</label>
                    <span className="text-gray-900">{resolveDisplayUsername(subscriber)}</span>
                  </div>
                  {resolveNetworkUsername(subscriber) && (
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">MAC / PPPoE</label>
                      <span className="text-gray-900 font-mono text-sm">
                        {resolveNetworkUsername(subscriber)}
                      </span>
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Package</label>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{subscriber.package.name || 'N/A'}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 block mb-1">Zone / Node</label>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {subscriber.zone.name || 'N/A'}
                        {subscriber.node.name && ` / ${subscriber.node.name}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'billing' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gray-400" />
                Billing Information
              </h3>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-sm text-gray-500 block mb-1">Outstanding Amount</label>
                  <p
                    className={`text-3xl font-bold ${
                      hasOutstanding ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    ₹{subscriber.billing.outstandingAmount.toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-sm text-gray-500 block mb-1">Billing Profile</label>
                  <p className="text-lg font-medium text-gray-900">
                    {subscriber.billing.profile || 'Default'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500 block mb-1">Due Date</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">
                      {subscriber.billing.dueDate
                        ? new Date(subscriber.billing.dueDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                        : 'Not set'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500 block mb-1">Expiry Date</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">
                      {subscriber.billing.expiryDate
                        ? new Date(subscriber.billing.expiryDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                        : 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              {hasOutstanding && isEligible && !subscriber.reminderPaused && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    This subscriber has an outstanding balance and is eligible for reminders.
                  </p>
                </div>
              )}

              {subscriber.reminderDelivery && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <h4 className="font-medium text-gray-900 mb-4">Reminder Delivery</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-500 mb-1">Last Reminder Sent</p>
                      <p className="font-medium text-gray-900">
                        {subscriber.reminderDelivery.lastReminderSentAt
                          ? new Date(subscriber.reminderDelivery.lastReminderSentAt).toLocaleString(
                              'en-IN'
                            )
                          : 'No reminders sent yet'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-500 mb-1">WhatsApp Reminder Status</p>
                      <p className="font-medium text-gray-900">
                        {subscriber.reminderDelivery.whatsapp.label}
                      </p>
                      {subscriber.reminderDelivery.whatsapp.lastSentAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(
                            subscriber.reminderDelivery.whatsapp.lastSentAt
                          ).toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 sm:col-span-2">
                      <p className="text-gray-500 mb-1">Email Reminder Status</p>
                      <p className="font-medium text-gray-900">
                        {subscriber.reminderDelivery.email.label}
                      </p>
                      {subscriber.reminderDelivery.email.lastSentAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(subscriber.reminderDelivery.email.lastSentAt).toLocaleString(
                            'en-IN'
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Reminder History</h3>
                {reminderHistoryLoading ? (
                  <p className="text-sm text-gray-500">Loading reminder history...</p>
                ) : (reminderHistory?.reminders.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500">No reminder history yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                          <th className="py-2 pr-4">Date</th>
                          <th className="py-2 pr-4">Channel</th>
                          <th className="py-2 pr-4">Source</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Reason</th>
                          <th className="py-2 pr-4">Invoice</th>
                          <th className="py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reminderHistory?.reminders.map((item) => (
                          <tr key={item.id}>
                            <td className="py-3 pr-4 text-gray-700">
                              {new Date(item.sentAt || item.createdAt).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 pr-4 capitalize">{item.channel || 'Not set'}</td>
                            <td className="py-3 pr-4 capitalize">{item.source || 'Not set'}</td>
                            <td className="py-3 pr-4 capitalize">{item.status}</td>
                            <td className="py-3 pr-4 text-gray-600">{item.skipReason || 'None'}</td>
                            <td className="py-3 pr-4">{item.invoiceNumber || 'Not set'}</td>
                            <td className="py-3 text-right font-medium">
                              {item.amount != null ? `₹${Number(item.amount).toFixed(2)}` : 'Not set'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Sync History</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Last Updated</p>
                      <p className="text-xs text-gray-500">
                        {new Date(subscriber.updatedAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <span className="text-sm text-gray-600">Database</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Created</p>
                      <p className="text-xs text-gray-500">
                        {new Date(subscriber.createdAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <span className="text-sm text-gray-600">Database</span>
                  </div>
                  {subscriber.lastSeenInCrm && (
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Last Seen in CRM</p>
                        <p className="text-xs text-gray-500">
                          {new Date(subscriber.lastSeenInCrm).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <span className="text-sm text-gray-600">CRM</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/invoices?subscriber=${subscriber.id}`)}
                className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                View Invoices
                <ExternalLink className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={() => navigate(`/reminders?subscriber=${subscriber.id}`)}
                className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                View Reminders
                <ExternalLink className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Status Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Status Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">CRM Status</span>
                <span className="text-sm font-medium text-gray-900">{subscriber.status.raw}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Normalized Status</span>
                <StatusBadge status={subscriber.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Phone Valid</span>
                <span
                  className={`text-sm font-medium ${
                    subscriber.phone.valid ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {subscriber.phone.valid ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Reminders</span>
                <span
                  className={`text-sm font-medium ${
                    subscriber.reminderPaused ? 'text-yellow-600' : 'text-green-600'
                  }`}
                >
                  {subscriber.reminderPaused ? 'Paused' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">
              ID: {subscriber.id} | CRM ID: {subscriber.crmSubscriberId}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
